import React, { useEffect, useMemo, useState } from 'react'

import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'

import {
  createAccessPassTemplate,
  getAccessPassEvents,
  updateAccessPassTemplate,
  upload,
} from '../../utilities/api'
import { b64toBlob } from '../../utilities/helpers'
import AuthService from '../../utilities/services/auth.service'

import { UploadImage } from '../UploadImage'
import { TrashButton } from '../TrashButton'
import { DateInputWrapper } from '../DateInputWrapper'
import { TimeInputWrapper } from '../TimeInputWrapper'
import { DeleteModal } from '../DeleteModal'
import { AddPricingTierModal } from '../PackageDetailsWrapper/Inventory/AddPricingTierModal'
import { PricingTier } from '../PackageDetailsWrapper/Inventory/PricingTier'
import { ColorField, normalizeHexColor } from './ColorField'
import { AccessPassTemplatePreview } from './AccessPassTemplatePreview'

const DEFAULT_COLORS = {
  backgroundColor: '#23262f',
  fontColor: '#fcfcfd',
  primaryColor: '#9757d7',
}

function toDateValue(isoString) {
  if (!isoString) return null
  const date = new Date(isoString)
  return Number.isNaN(date.getTime()) ? null : date
}

function fromDateValue(value) {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

export function CreateAccessPassTemplateModal({
  show,
  setShow,
  organizationUUID,
  template,
  onSaved,
}) {
  const [name, setName] = useState('')
  const [events, setEvents] = useState([])
  const [selectedEventUUIDs, setSelectedEventUUIDs] = useState([])
  const [backgroundColor, setBackgroundColor] = useState(
    DEFAULT_COLORS.backgroundColor
  )
  const [fontColor, setFontColor] = useState(DEFAULT_COLORS.fontColor)
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_COLORS.primaryColor)
  const [artwork, setArtwork] = useState('')
  const [initialArtwork, setInitialArtwork] = useState('')
  const [artworkCoordinates, setArtworkCoordinates] = useState()
  const [availableOnline, setAvailableOnline] = useState(false)
  const [pricingTiers, setPricingTiers] = useState({})
  const [onlinePricingTierId, setOnlinePricingTierId] = useState('')
  const [pricingTier, setPricingTier] = useState()
  const [showPricingTierModal, setShowPricingTierModal] = useState(false)
  const [pricingTierToDelete, setPricingTierToDelete] = useState()
  const [showDeletePricingTier, setShowDeletePricingTier] = useState(false)
  const [qtyAvailable, setQtyAvailable] = useState('')
  const [useFlatServiceFee, setUseFlatServiceFee] = useState(false)
  const [flatServiceFee, setFlatServiceFee] = useState('')
  const [onlineStart, setOnlineStart] = useState(null)
  const [onlineEnd, setOnlineEnd] = useState(null)
  const [hideOnline, setHideOnline] = useState(false)
  const [wasAvailableOnline, setWasAvailableOnline] = useState(false)
  const [loadingEvents, setLoadingEvents] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const isEditing = !!template
  const isSuperAdmin = AuthService.isSuperAdminOrganizer()

  useEffect(() => {
    if (!show || !organizationUUID) return

    const nextArtwork = template?.artwork?.url || ''
    setName(template?.name || '')
    setSelectedEventUUIDs(
      (template?.events || []).map((eventItem) => eventItem.uuid)
    )
    setBackgroundColor(
      template?.backgroundColor || DEFAULT_COLORS.backgroundColor
    )
    setFontColor(template?.fontColor || DEFAULT_COLORS.fontColor)
    setPrimaryColor(template?.primaryColor || DEFAULT_COLORS.primaryColor)
    setArtwork(nextArtwork)
    setInitialArtwork(nextArtwork)
    setArtworkCoordinates()
    setAvailableOnline(Boolean(template?.availableOnline))
    setWasAvailableOnline(Boolean(template?.availableOnline))
    const templatePricingTiers =
      template?.pricingTiers?.length > 0
        ? template.pricingTiers
        : template?.price != null && template?.price !== ''
          ? [{ id: 1, name: 'Access Pass', price: template.price }]
          : []
    const nextPricingTiers = Object.fromEntries(
      templatePricingTiers.map((tier, index) => [
        tier.id ?? index + 1,
        { ...tier, id: tier.id ?? index + 1 },
      ])
    )
    const onlineTier =
      templatePricingTiers[template?.onlinePricingTierIndex ?? 0]
    setPricingTiers(nextPricingTiers)
    setOnlinePricingTierId(
      template?.availableOnline && onlineTier ? String(onlineTier.id) : ''
    )
    setQtyAvailable(
      template?.qtyAvailable != null && template?.qtyAvailable !== ''
        ? String(template.qtyAvailable)
        : ''
    )
    setUseFlatServiceFee(Boolean(template?.useFlatServiceFee))
    setFlatServiceFee(
      template?.flatServiceFee != null && template?.flatServiceFee !== ''
        ? String(template.flatServiceFee)
        : ''
    )
    setOnlineStart(toDateValue(template?.onlineStart))
    setOnlineEnd(toDateValue(template?.onlineEnd))
    setHideOnline(template?.availableOnline && template?.isVisible === false)
    setError('')
    setLoadingEvents(true)

    getAccessPassEvents(organizationUUID)
      .then((res) => {
        setEvents(res?.data?.data || [])
      })
      .catch((err) => {
        console.error(err)
        setError('Could not load events for this organization.')
      })
      .finally(() => {
        setLoadingEvents(false)
      })
  }, [show, organizationUUID, template])

  const toggleEvent = (uuid) => {
    setSelectedEventUUIDs((current) =>
      current.includes(uuid)
        ? current.filter((id) => id !== uuid)
        : [...current, uuid]
    )
  }

  const handleShowPricingTier = (_, id) => {
    setPricingTier(id !== undefined ? pricingTiers[id] : undefined)
    setShowPricingTierModal(true)
  }

  const handleClosePricingTier = () => {
    setPricingTier()
    setShowPricingTierModal(false)
  }

  const handleAddPricingTier = (tier) => {
    const id =
      pricingTier?.id ??
      Math.max(0, ...Object.keys(pricingTiers).map((key) => Number(key))) + 1
    setPricingTiers((current) => ({
      ...current,
      [id]: { ...tier, id },
    }))
    handleClosePricingTier()
  }

  const confirmPricingTierRemoval = (id) => {
    setPricingTierToDelete(String(id))
    setShowDeletePricingTier(true)
  }

  const handleDeletePricingTier = () => {
    setPricingTiers((current) => {
      const updated = { ...current }
      delete updated[pricingTierToDelete]
      return updated
    })
    if (String(onlinePricingTierId) === String(pricingTierToDelete)) {
      setOnlinePricingTierId('')
    }
    setPricingTierToDelete()
    setShowDeletePricingTier(false)
  }

  const sampleEvent = useMemo(() => {
    const selectedEvents = events.filter((eventItem) =>
      selectedEventUUIDs.includes(eventItem.uuid)
    )
    return selectedEvents[0] || null
  }, [events, selectedEventUUIDs])

  const eventsBySeason = useMemo(() => {
    const groups = new Map()

    for (const eventItem of events) {
      const seasonKey = eventItem.season?.uuid || 'no-season'
      const existing = groups.get(seasonKey)
      if (existing) {
        existing.events.push(eventItem)
      } else {
        groups.set(seasonKey, {
          key: seasonKey,
          name: eventItem.season?.name || 'No season',
          start: eventItem.season?.start || null,
          events: [eventItem],
        })
      }
    }

    return Array.from(groups.values()).sort((a, b) => {
      if (a.key === 'no-season') return 1
      if (b.key === 'no-season') return -1
      if (a.start && b.start) {
        return new Date(b.start) - new Date(a.start)
      }
      return a.name.localeCompare(b.name)
    })
  }, [events])

  const previewBackgroundColor = normalizeHexColor(
    backgroundColor,
    DEFAULT_COLORS.backgroundColor
  )
  const previewFontColor = normalizeHexColor(
    fontColor,
    DEFAULT_COLORS.fontColor
  )
  const previewPrimaryColor = normalizeHexColor(
    primaryColor,
    DEFAULT_COLORS.primaryColor
  )

  const uploadArtworkIfNeeded = async () => {
    if (!artwork || artwork.startsWith('https://')) {
      return template?.artwork?.id || undefined
    }

    const block = artwork.split(';')
    const contentType = block[0].split(':')[1]
    const realData = block[1].split(',')[1]
    const formData = new FormData()
    formData.append('files', b64toBlob(realData, contentType))
    const res = await upload(formData)
    return res?.data?.[0]?.id ?? null
  }

  const resolveArtworkId = async () => {
    if (artwork === initialArtwork) {
      return undefined
    }

    if (!artwork) {
      return null
    }

    if (artwork.startsWith('https://')) {
      return template?.artwork?.id || undefined
    }

    return uploadArtworkIfNeeded()
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')

    const pricingTierList = Object.values(pricingTiers)
    if (pricingTierList.length === 0) {
      setError('At least one pricing tier is required.')
      return
    }

    const onlinePricingTierIndex = pricingTierList.findIndex(
      (tier) => String(tier.id) === String(onlinePricingTierId)
    )
    if (availableOnline) {
      if (onlinePricingTierIndex < 0) {
        setError('Select a pricing tier for the online purchase price.')
        return
      }
    }

    setSubmitting(true)
    try {
      const payload = {
        name,
        eventUUIDs: selectedEventUUIDs,
        backgroundColor: previewBackgroundColor,
        fontColor: previewFontColor,
        primaryColor: previewPrimaryColor,
        availableOnline,
        pricingTiers: pricingTierList.map(({ name, price: tierPrice }) => ({
          name,
          price: Number(tierPrice),
        })),
        onlinePricingTierIndex: availableOnline ? onlinePricingTierIndex : null,
        qtyAvailable:
          availableOnline && qtyAvailable !== ''
            ? Number.parseInt(qtyAvailable, 10)
            : null,
        onlineStart: availableOnline ? fromDateValue(onlineStart) : null,
        onlineEnd: availableOnline ? fromDateValue(onlineEnd) : null,
        isVisible: availableOnline ? !hideOnline : true,
        useFlatServiceFee: Boolean(useFlatServiceFee),
        flatServiceFee: useFlatServiceFee
          ? flatServiceFee === '' ||
            flatServiceFee === null ||
            flatServiceFee === undefined
            ? 0
            : Number(flatServiceFee)
          : null,
      }

      const artworkId = await resolveArtworkId()
      if (artworkId !== undefined) {
        payload.artworkId = artworkId
      }

      if (isEditing) {
        await updateAccessPassTemplate({
          templateId: template.uuid || template.id,
          ...payload,
        })
      } else {
        await createAccessPassTemplate({
          ...payload,
          organizationUUID,
        })
      }
      setShow(false)
      setName('')
      setSelectedEventUUIDs([])
      setArtwork('')
      setInitialArtwork('')
      onSaved?.()
    } catch (err) {
      setError(
        err?.response?.data?.error?.message ||
          `Failed to ${isEditing ? 'update' : 'create'} template`
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      size="xl"
      className="modal-full"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing ? 'Edit pass template' : 'Create pass template'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row className="g-4">
            <Col lg={8}>
              <Stack gap={3}>
                <p className="small text-muted mb-0">
                  Templates save a reusable set of events and default pass
                  styling. Select events and customize how issued passes look.
                </p>
                <Form.Group controlId="accessPassTemplateName">
                  <Form.Label>Template name</Form.Label>
                  <Form.Control
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Media access, VIP backstage"
                    required
                  />
                </Form.Group>

                <div>
                  <Form.Label>Pass artwork</Form.Label>
                  <p className="small text-muted mb-2">
                    Optional banner image shown at the top of the pass.
                  </p>
                  <div
                    className={artwork ? 'flex gap-3 align-items-start' : ''}
                  >
                    <UploadImage
                      dropzoneSize="sm"
                      dropzoneWithRadius
                      selectedImage={artwork}
                      setSelectedImage={setArtwork}
                      coordinates={artworkCoordinates}
                      setCoordinates={setArtworkCoordinates}
                      skipCrop
                      aspectRatio={16 / 9}
                      cropMaxWidth={1600}
                      cropMaxHeight={900}
                      size="md"
                      altText={`Artwork for ${name || 'access pass template'}`}
                    />
                    {artwork && (
                      <TrashButton
                        onClick={() => {
                          setArtwork('')
                          setArtworkCoordinates()
                        }}
                        variant="danger"
                      />
                    )}
                  </div>
                </div>

                <div>
                  <Form.Label>Pass colors</Form.Label>
                  <Stack gap={3} className="mt-2">
                    <ColorField
                      id="accessPassTemplateBackgroundColor"
                      label="Background color"
                      value={backgroundColor}
                      onChange={setBackgroundColor}
                    />
                    <ColorField
                      id="accessPassTemplateFontColor"
                      label="Font color"
                      value={fontColor}
                      onChange={setFontColor}
                    />
                    <ColorField
                      id="accessPassTemplatePrimaryColor"
                      label="Primary color"
                      value={primaryColor}
                      onChange={setPrimaryColor}
                    />
                  </Stack>
                </div>

                <div>
                  <Form.Label>Events</Form.Label>
                  {loadingEvents ? (
                    <p className="small text-muted mb-0 mt-2">
                      Loading events...
                    </p>
                  ) : events.length === 0 ? (
                    <p className="small text-muted mb-0 mt-2">
                      No eligible events found for this organization.
                    </p>
                  ) : (
                    <Stack
                      gap={3}
                      className="mt-2"
                      style={{ maxHeight: 360, overflowY: 'auto' }}
                    >
                      {eventsBySeason.map((seasonGroup) => (
                        <div key={seasonGroup.key}>
                          <p className="small fw-semibold text-muted text-uppercase mb-2">
                            {seasonGroup.name}
                          </p>
                          <Stack gap={2}>
                            {seasonGroup.events.map((eventItem) => (
                              <Form.Check
                                key={eventItem.uuid}
                                type="checkbox"
                                id={`access-pass-template-event-${eventItem.uuid}`}
                                className="mb-0"
                                style={{ whiteSpace: 'normal' }}
                                label={`${eventItem.name}${eventItem.status ? ` (${eventItem.status})` : ''}`}
                                checked={selectedEventUUIDs.includes(
                                  eventItem.uuid
                                )}
                                onChange={() => toggleEvent(eventItem.uuid)}
                              />
                            ))}
                          </Stack>
                        </div>
                      ))}
                    </Stack>
                  )}
                </div>

                <div>
                  <Form.Label>Pricing tiers</Form.Label>
                  <p className="small text-muted mb-2">
                    Create the prices available when assigning this access pass.
                  </p>
                  {Object.values(pricingTiers).length > 0 && (
                    <Stack as="ul" gap={2} className="mb-3 p-0">
                      {Object.values(pricingTiers).map((tier) => (
                        <PricingTier
                          key={tier.id}
                          pricingTier={tier}
                          handleShow={handleShowPricingTier}
                          confirmRemoval={confirmPricingTierRemoval}
                        />
                      ))}
                    </Stack>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-light"
                    className="btn-plus"
                    onClick={handleShowPricingTier}
                  >
                    Add tier
                  </Button>
                </div>

                <div>
                  <Form.Check
                    type="switch"
                    id="accessPassTemplateAvailableOnline"
                    label="Available to purchase online"
                    checked={availableOnline}
                    onChange={(e) => setAvailableOnline(e.target.checked)}
                    className="form-switch--with-desc"
                  />
                  <p className="small text-muted mb-0 mt-1">
                    When enabled, this pass appears on the venue Packages tab
                    and can be purchased through checkout.
                  </p>
                </div>

                {isSuperAdmin && (
                  <div>
                    <Form.Label>Service fee</Form.Label>
                    <p className="small text-muted mb-2">
                      Optionally charge one service fee for the whole pass
                      instead of the organization&apos;s access pass fee rules.
                    </p>
                    <Form.Check
                      type="checkbox"
                      id="accessPassTemplateUseFlatServiceFee"
                      label="Charge a single service fee for this pass"
                      checked={useFlatServiceFee}
                      onChange={(e) => setUseFlatServiceFee(e.target.checked)}
                      className="mb-0"
                    />
                    {useFlatServiceFee && (
                      <Form.Group
                        controlId="accessPassTemplateFlatServiceFee"
                        className="mt-3"
                      >
                        <Form.Label>Pass service fee</Form.Label>
                        <Form.Control
                          type="text"
                          inputMode="decimal"
                          pattern="^[0-9.]*$"
                          placeholder="0.00"
                          value={flatServiceFee}
                          onChange={(e) => {
                            const next = e.target.value
                            if (next === '' || /^[0-9.]*$/.test(next)) {
                              setFlatServiceFee(next)
                            }
                          }}
                        />
                        <Form.Text className="text-muted">
                          Charged once per pass purchase. Leaves processing fees
                          and taxes unchanged.
                        </Form.Text>
                      </Form.Group>
                    )}
                  </div>
                )}

                {availableOnline && (
                  <Stack gap={3}>
                    <Form.Group controlId="accessPassTemplateOnlinePricingTier">
                      <Form.Label>Online price tier</Form.Label>
                      <Form.Select
                        value={onlinePricingTierId}
                        onChange={(e) => setOnlinePricingTierId(e.target.value)}
                        required
                      >
                        <option value="">Select a pricing tier</option>
                        {Object.values(pricingTiers).map((tier) => (
                          <option key={tier.id} value={tier.id}>
                            {tier.name} (${Number(tier.price).toFixed(2)})
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        {useFlatServiceFee
                          ? `This tier's price is displayed online and uses the pass service fee override${
                              flatServiceFee !== ''
                                ? ` ($${flatServiceFee})`
                                : ''
                            } at checkout when the price is greater than $0.`
                          : "This tier's price is displayed online and uses organization access pass fee rules at checkout when greater than $0 (defaults to a $1 Blocktickets service fee when no rule matches)."}
                      </Form.Text>
                    </Form.Group>

                    <Form.Group controlId="accessPassTemplateQtyAvailable">
                      <Form.Label>Quantity available (optional)</Form.Label>
                      <Form.Control
                        type="text"
                        inputMode="numeric"
                        pattern="^[0-9]*$"
                        placeholder="Unlimited"
                        value={qtyAvailable}
                        onChange={(e) => {
                          const next = e.target.value
                          if (next === '' || /^[0-9]*$/.test(next)) {
                            setQtyAvailable(next)
                          }
                        }}
                      />
                      <Form.Text className="text-muted">
                        Leave blank for unlimited availability.
                      </Form.Text>
                    </Form.Group>

                    <Stack
                      direction="horizontal"
                      gap={4}
                      className="align-items-stretch flex-wrap"
                    >
                      <fieldset className="flex-grow-1">
                        <legend className="sm">
                          Available from (optional)
                        </legend>
                        <Stack
                          direction="horizontal"
                          className="align-items-stretch"
                          gap={3}
                        >
                          <DateInputWrapper
                            id="access-pass-online-start"
                            setDate={setOnlineStart}
                            selectedDate={onlineStart}
                            startDate={onlineStart}
                            maxDate={onlineEnd || undefined}
                            showMinDate={false}
                            size="sm"
                          />
                          <TimeInputWrapper
                            id="access-pass-online-start-time"
                            setDate={setOnlineStart}
                            selectedDate={onlineStart}
                            shouldFilterTimes={false}
                            size="sm"
                          />
                        </Stack>
                      </fieldset>

                      <fieldset className="flex-grow-1">
                        <legend className="sm">
                          Available until (optional)
                        </legend>
                        <Stack
                          direction="horizontal"
                          className="align-items-stretch"
                          gap={3}
                        >
                          <DateInputWrapper
                            id="access-pass-online-end"
                            setDate={setOnlineEnd}
                            selectedDate={onlineEnd}
                            startDate={onlineStart}
                            endDate={onlineEnd}
                            minDate={onlineStart || undefined}
                            showMinDate={Boolean(onlineStart)}
                            size="sm"
                          />
                          <TimeInputWrapper
                            id="access-pass-online-end-time"
                            setDate={setOnlineEnd}
                            selectedDate={onlineEnd}
                            shouldFilterTimes={false}
                            size="sm"
                          />
                        </Stack>
                      </fieldset>
                    </Stack>

                    {(wasAvailableOnline || availableOnline) && (
                      <Form.Check
                        type="switch"
                        id="accessPassTemplateHideOnline"
                        label="Hide from online purchase"
                        checked={hideOnline}
                        onChange={(e) => setHideOnline(e.target.checked)}
                        className="form-switch--with-desc"
                      />
                    )}
                    {(wasAvailableOnline || availableOnline) && hideOnline && (
                      <p className="small text-muted mb-0">
                        Hidden passes stay off the venue Packages tab and cannot
                        be purchased online.
                      </p>
                    )}
                  </Stack>
                )}

                {error && <p className="text-danger mb-0">{error}</p>}
              </Stack>
            </Col>
            <Col lg={4}>
              <div className="sticky-top" style={{ top: '1rem' }}>
                <AccessPassTemplatePreview
                  name={name}
                  backgroundColor={previewBackgroundColor}
                  fontColor={previewFontColor}
                  primaryColor={previewPrimaryColor}
                  artwork={artwork}
                  eventCount={selectedEventUUIDs.length}
                  sampleEvent={sampleEvent}
                />
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting || !selectedEventUUIDs.length}
          >
            {submitting
              ? isEditing
                ? 'Saving...'
                : 'Creating...'
              : isEditing
                ? 'Save template'
                : 'Create template'}
          </Button>
        </Modal.Footer>
      </Form>
      <AddPricingTierModal
        show={showPricingTierModal}
        pricingTier={pricingTier}
        handleAdd={handleAddPricingTier}
        handleClose={handleClosePricingTier}
      />
      <DeleteModal
        entity="pricing tier"
        show={showDeletePricingTier}
        handleDelete={handleDeletePricingTier}
        handleClose={() => {
          setPricingTierToDelete()
          setShowDeletePricingTier(false)
        }}
      />
    </Modal>
  )
}
